export interface ParsedPromotion {
  name: string;
  discountPercent: number | null;
  discountValue: number | null;
}

export interface ParsedConditions {
  zampAmount: number;
  zampPercent: number | null;
  zpe1Amount: number;
  zpe1Percent: number | null;
  zdcpAmount: number;
  zacpAmount: number;
  promoAmount: number;
  promotions: ParsedPromotion[];
}

export const COST_CONDITIONS = new Set([
  'ZPRO',
  'ZMKP',
  'ZCOP',
  'ZCOM',
  'ZJUR',
  'ZBON',
  'ZCLU',
  'ZEST',
  'ZFRE',
  'ZAMP',
  'ZPE1',
  'ZDCP',
  'ZACP',
  'BX13',
  'BX23',
  'BX41',
  'BX51',
  'BX52',
]);

/**
 * Parses a pipe-delimited conditions array into structured financial data.
 * Mirrors the backend helper at core-commerce/src/modules/order/helpers/order-conditions.helper.ts
 *
 * Format (see condition-steps.helper.ts for authoritative definition):
 *  - p[1]  = condition code
 *  - p[2]  = description (readable name)
 *  - p[4]  = conditionRate (percentage)
 *  - p[7]  = calculatedAmount (monetary impact in R$)
 *  - p[9]  = conditionClass ('A'=discount | 'B'=surcharge/price | 'C'=tax)
 *  - p[13] = isApplied ('1' = applied)
 *  - p[14] = isManual ('1' = manual condition by seller)
 *
 * Used by: DefaultLocalOrderHistoryService.mapToDetail(), DefaultLocalOrderHistoryService.getProductPriceHistory()
 */
export function parseConditions(conditions: string[]): ParsedConditions {
  const result: ParsedConditions = {
    zampAmount: 0,
    zampPercent: null,
    zpe1Amount: 0,
    zpe1Percent: null,
    zdcpAmount: 0,
    zacpAmount: 0,
    promoAmount: 0,
    promotions: [],
  };

  for (const raw of conditions) {
    const p = raw.split('|');
    if (p[13] !== '1') continue; // skip inactive conditions

    const code = p[1] ?? '';
    const description = p[2] ?? ''; // [2] = description / readable name
    const conditionRate = parseFloat(p[4] ?? '0') || 0; // [4] = conditionRate (%)
    const amount = parseFloat(p[7] ?? '0') || 0; // [7] = calculatedAmount (R$)
    const condClass = p[9] ?? 'B';
    const isManual = p[14] === '1';
    const isPromo = condClass === 'A' && !isManual && !COST_CONDITIONS.has(code);

    if (code === 'ZAMP') {
      result.zampAmount += amount;
      result.zampPercent = conditionRate > 0 ? conditionRate : null;
    } else if (code === 'ZPE1') {
      result.zpe1Amount += amount;
      result.zpe1Percent = conditionRate > 0 ? conditionRate : null;
    } else if (code === 'ZDCP') result.zdcpAmount += amount;
    else if (code === 'ZACP') result.zacpAmount += amount;
    else if (isPromo)
      result.promotions.push({
        name: description,
        discountPercent: conditionRate > 0 ? conditionRate : null,
        discountValue: amount > 0 ? amount : null,
      });
  }

  return result;
}
